import React, { MutableRefObject, useMemo, useRef } from 'react'
import { Flex, TableColumn, TableSquare, useScrollVisible, Text, TextVariant, Loading } from '@mprolab/uikit'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'
import { LicenseInSessionLogData, SessionLogInCycleByNodeIdData } from '@mprolab/sdk'
import { MediaQuery } from '@mprolab/uikit/src/theme/base'
import InfiniteScroll from 'react-infinite-scroll-component'
import { observer } from 'mobx-react-lite'

dayjs.extend(isBetween)

const List = styled(Flex)<{ scrollable: boolean }>`
  overflow-y: auto;
  max-height: 543px;
  flex-direction: column;
  padding-right: ${({ scrollable }) => (scrollable ? '16px' : 'unset')};
  width: 100%;
`

interface SessionsPerNodeLogViewProps {
  logData: SessionLogInCycleByNodeIdData[]
  sessionsCount: number
  dataLengthToFetch: number
  loadMoreSessions: (licenseId: string) => Promise<void>
  license?: LicenseInSessionLogData
}

const SessionsPerNodeLogView: React.FC<SessionsPerNodeLogViewProps> = observer(
  ({ logData, license, dataLengthToFetch, loadMoreSessions, sessionsCount }) => {
    const { t } = useTranslation()
    const wrapper = useRef<HTMLDivElement>(null)
    const scrollVisible = useScrollVisible(wrapper as MutableRefObject<HTMLDivElement>)

    const columns = useMemo(
      (): TableColumn[] => [
        {
          key: 'launcherId',
          name: t('rewards.launcherId'),
          gridColumns: 4,
          customComponent(log: SessionLogInCycleByNodeIdData) {
            return (
              <Text variant={TextVariant.BODY_SMALL} isTrimmed linesToDisplay={1}>
                {log.instanceId}
              </Text>
            )
          }
        },
        {
          key: 'ipAddress',
          name: t('rewards.ipAddress'),
          gridColumns: 2,
          align: 'flex-end',
          customComponent(log: SessionLogInCycleByNodeIdData) {
            return (
              <Flex justifyContent="flex-end">
                <Text variant={TextVariant.BODY_SMALL_BOLD}>{log.ipAddress}</Text>
              </Flex>
            )
          }
        },
        {
          key: 'workingTime',
          name: t('rewards.startTime'),
          gridColumns: 3,
          align: 'flex-end',
          customComponent(log: SessionLogInCycleByNodeIdData) {
            return (
              <Flex justifyContent="flex-end">
                <Text variant={TextVariant.BODY_SMALL_BOLD}>{dayjs.unix(log.startTime).format('MM/DD/YY, HH:mm')}</Text>
              </Flex>
            )
          }
        },
        {
          key: 'credits',
          name: t('rewards.endTime'),
          gridColumns: 3,
          align: 'flex-end',
          customComponent(log: SessionLogInCycleByNodeIdData) {
            return (
              <Flex justifyContent="flex-end">
                <Text variant={TextVariant.BODY_SMALL_BOLD}>{dayjs.unix(log.endTime).format('MM/DD/YY, HH:mm')}</Text>
              </Flex>
            )
          }
        }
      ],
      [logData.length]
    )

    return (
      <Flex flexDirection="column">
        <Text variant={TextVariant.BODY_BIG_BOLD}>{license?.name || license?.licenseId}</Text>
        <List ref={wrapper} scrollable={scrollVisible} id="nodesPerCycleLogs" mt="16px">
          <InfiniteScroll
            next={async () => loadMoreSessions(license?.licenseId)}
            hasMore={dataLengthToFetch < sessionsCount}
            loader={<Loading />}
            dataLength={dataLengthToFetch}
            scrollThreshold={0.99}
            scrollableTarget="nodesPerCycleLogs"
          >
            <TableSquare
              plain
              loading={false}
              data={logData}
              emptyListComponent={<Text textAlign="center">{t('rewards.noActiveLogs')}</Text>}
              fullViewMediaquery={MediaQuery.tablet}
              fullViewCustomWidth={900}
              {...{ columns }}
            />
          </InfiniteScroll>
        </List>
      </Flex>
    )
  }
)

export default SessionsPerNodeLogView
